/**
 * users-new.ts
 * 
 * Servizio per la gestione degli utenti tramite API.
 * Allineato con le API del backend UfficioStile.
 */

import { apiClient, buildQueryParams } from './base';
import {
  User,
  UserCreateRequest,
  UserUpdateRequest,
  UserRole,
  UserSummary,
  ApiResponse,
  PagedResponse,
  SearchParams
} from '../../types/api';

export class UserService {
  private baseUrl = '/api/User';

  // ================== OPERAZIONI CRUD BASE ==================
  async getAllUsers(params?: SearchParams): Promise<PagedResponse<User>> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    const response = await apiClient.get<PagedResponse<User>>(url);
    return response.data;
  }

  async getUserSummaries(params?: SearchParams): Promise<PagedResponse<User>> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}/summaries?${queryString}` : `${this.baseUrl}/summaries`;
    const response = await apiClient.get<PagedResponse<User>>(url);
    return response.data;
  }

  async getUserById(id: string): Promise<User> {
    const response = await apiClient.get<User>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async createUser(data: UserCreateRequest): Promise<User> {
    const response = await apiClient.post<User>(this.baseUrl, data);
    return response.data;
  }

  async updateUser(id: string, data: UserUpdateRequest): Promise<User> {
    const response = await apiClient.put<User>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  // ================== OPERAZIONI FILTRI ==================
  async getUsersByRole(role: UserRole): Promise<User[]> {
    const response = await apiClient.get<User[]>(`${this.baseUrl}/by-role/${role}`);
    return response.data;
  }

  async getActiveUsers(): Promise<User[]> {
    const response = await apiClient.get<User[]>(`${this.baseUrl}/active`);
    return response.data;
  }

  // ================== OPERAZIONI STATO ==================
  async activateUser(id: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/activate`);
  }

  async deactivateUser(id: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/deactivate`);
  }

  async assignRole(id: string, role: UserRole): Promise<void> {
    const data = { role };
    await apiClient.post(`${this.baseUrl}/${id}/assign-role`, data);
  }

  // ================== OPERAZIONI RICERCA ==================
  async searchUsers(params: {
    searchTerm?: string;
    role?: UserRole;
    isActive?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<PagedResponse<User>> {
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<PagedResponse<User>>(`${this.baseUrl}/search?${queryString}`);
    return response.data;
  }

  // ================== STATISTICHE ==================
  async getUserStatistics(id: string): Promise<any> {
    const response = await apiClient.get<any>(`${this.baseUrl}/${id}/statistics`);
    return response.data;
  }

  async getUsersWithActiveTasks(): Promise<User[]> {
    const response = await apiClient.get<User[]>(`${this.baseUrl}/with-active-tasks`);
    return response.data;
  }

  async getUsersByWorkload(): Promise<User[]> {
    const response = await apiClient.get<User[]>(`${this.baseUrl}/by-workload`);
    return response.data;
  }

  /**
   * Elenco utenti "semplice" (id, fullName, email, colorTag) per Select/MultiSelect assegnatari.
   * Gestisce difensivamente sia una risposta array nudo sia una risposta `{ data: [...] }`
   * avvolta, come già fa `taskService.getUsers()` legacy per lo stesso endpoint.
   */
  async getSimpleUsers(): Promise<UserSummary[]> {
    const response: unknown = await apiClient.get<UserSummary[]>(`${this.baseUrl}/simple`);
    if (Array.isArray(response)) {
      return response as UserSummary[];
    }
    const data = (response as ApiResponse<UserSummary[]> | undefined)?.data;
    return Array.isArray(data) ? data : [];
  }
}

export const userService = new UserService();
